// src/pages/admin/DataQualityDashboard.tsx - Dashboard de Qualidade de Dados

import React, { useState, useEffect, useMemo } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Wrench,
  Eye,
  Database,
  TrendingUp,
  Filter,
  Search,
  Calendar,
  ClipboardList,
} from 'lucide-react';
import {
  auditPatientData,
  normalizeAllPatientData,
  normalizePatient,
  formatAuditReport,
  fixAppointmentIssue,
  AuditReport,
  PatientDataIssue,
  AppointmentAuditIssue,
} from '../../utils/dataAudit';
import DataQualityBadge from '../../components/admin/DataQualityBadge';
import { toast } from 'react-toastify';
import { matchesPatientSearch, normalizeSearchText } from '../../utils/patientSearch';

type FilterType = 'all' | 'critical' | 'warning' | 'info' | 'healthy';

export default function DataQualityDashboard() {
  const [report, setReport] = useState<AuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIssue, setSelectedIssue] = useState<PatientDataIssue | null>(null);
  const [appointmentDateFilter, setAppointmentDateFilter] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [fixingAppointment, setFixingAppointment] = useState<string | null>(null);

  useEffect(() => {
    runAudit();
  }, []);

  const runAudit = async () => {
    try {
      setLoading(true);
      toast.info('Iniciando auditoria de dados...');

      const auditReport = await auditPatientData();
      setReport(auditReport);

      const appointmentIssuesCount = auditReport.appointmentAudit?.issues.length || 0;

      if (auditReport.patientsWithIssues === 0 && appointmentIssuesCount === 0) {
        toast.success('✅ Pacientes e agendamentos estão com dados consistentes!');
      } else {
        toast.warning(
          `⚠️ ${auditReport.patientsWithIssues} paciente(s) e ${appointmentIssuesCount} agendamento(s) com problemas encontrados`
        );
      }
    } catch (error: any) {
      console.error('Erro na auditoria:', error);
      toast.error('Erro ao executar auditoria: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const runNormalization = async () => {
    if (
      !confirm(
        'Tem certeza que deseja normalizar TODOS os dados de pacientes?\n\n' +
          'Esta ação irá:\n' +
          '- Consolidar campos duplicados\n' +
          '- Criar estruturas financeiras faltantes\n' +
          '- Adicionar valores padrão onde necessário\n\n' +
          'Esta operação pode levar alguns minutos.'
      )
    ) {
      return;
    }

    try {
      setNormalizing(true);
      toast.info('Iniciando normalização de dados...');

      const result = await normalizeAllPatientData();

      if (result.success) {
        toast.success(
          `✅ Normalização concluída!\n\n` +
            `Processados: ${result.patientsProcessed}\n` +
            `Corrigidos: ${result.patientsFixed}`
        );
      } else {
        toast.warning(
          `⚠️ Normalização concluída com erros:\n\n` +
            `Processados: ${result.patientsProcessed}\n` +
            `Corrigidos: ${result.patientsFixed}\n` +
            `Erros: ${result.errors.length}`
        );
      }

      // Executar nova auditoria
      await runAudit();
    } catch (error: any) {
      console.error('Erro na normalização:', error);
      toast.error('Erro ao normalizar dados: ' + error.message);
    } finally {
      setNormalizing(false);
    }
  };

  const fixSinglePatient = async (patientId: string) => {
    try {
      toast.info('Normalizando paciente...');
      const success = await normalizePatient(patientId);

      if (success) {
        toast.success('✅ Paciente normalizado com sucesso!');
        await runAudit();
        setSelectedIssue(null);
      } else {
        toast.error('❌ Erro ao normalizar paciente');
      }
    } catch (error: any) {
      console.error('Erro ao normalizar paciente:', error);
      toast.error('Erro: ' + error.message);
    }
  };

  const downloadReport = () => {
    if (!report) return;

    const formatted = formatAuditReport(report);
    const blob = new Blob([formatted], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-qualidade-dados-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('✅ Relatório baixado com sucesso!');
  };

  const filteredIssues = report?.issues.filter(issue => {
    // Filtro por severidade
    if (filter !== 'all' && issue.severity !== filter) {
      return false;
    }

    // Filtro por busca
    if (searchTerm) {
      const term = normalizeSearchText(searchTerm);
      return (
        matchesPatientSearch(searchTerm, issue.patientName, [issue.patientId]) ||
        issue.missingFields.some(f => normalizeSearchText(f).includes(term)) ||
        issue.inconsistentFields.some(f => normalizeSearchText(f).includes(term))
      );
    }

    return true;
  }) || [];

  const filteredAppointmentIssues = useMemo(() => {
    if (!report?.appointmentAudit) {
      return [] as AppointmentAuditIssue[];
    }

    const startFilter = appointmentDateFilter.start ? new Date(appointmentDateFilter.start) : null;
    const endFilter = appointmentDateFilter.end ? new Date(appointmentDateFilter.end) : null;
    if (endFilter) {
      endFilter.setHours(23, 59, 59, 999);
    }

    const filtered = report.appointmentAudit.issues.filter(issue => {
      const referenceDateString = issue.createdAt || issue.startDate;
      if (!referenceDateString) {
        return true;
      }

      const referenceDate = new Date(referenceDateString);
      if (Number.isNaN(referenceDate.getTime())) {
        return true;
      }

      if (startFilter && referenceDate < startFilter) {
        return false;
      }

      if (endFilter && referenceDate > endFilter) {
        return false;
      }

      return true;
    });

    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.startDate || 0).getTime();
      const dateB = new Date(b.createdAt || b.startDate || 0).getTime();
      return dateB - dateA;
    });
  }, [report, appointmentDateFilter]);

  const handleFixAppointmentIssue = async (issue: AppointmentAuditIssue) => {
    try {
      setFixingAppointment(issue.appointmentId);
      const result = await fixAppointmentIssue(issue);
      if (result.success) {
        toast.success('Agendamento corrigido com sucesso!');
        await runAudit();
      } else {
        toast.warning(result.message || 'Não foi possível aplicar a correção automática.');
      }
    } catch (error: any) {
      console.error('Erro ao corrigir agendamento:', error);
      toast.error('Erro ao corrigir agendamento: ' + error.message);
    } finally {
      setFixingAppointment(null);
    }
  };

  if (loading && !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Analisando dados dos pacientes...</p>
        </div>
      </div>
    );
  }

  const healthPercentage = report
    ? ((report.patientsHealthy / report.totalPatients) * 100).toFixed(1)
    : '0';
  const hasAppointmentIssues = Boolean(report?.appointmentAudit?.issues.length);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-600" />
              Qualidade de Dados
            </h1>
            <p className="text-gray-600 mt-1">
              Auditoria e normalização dos dados de pacientes
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={runAudit}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>

            {report && report.patientsWithIssues > 0 && (
              <button
                onClick={runNormalization}
                disabled={normalizing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Wrench className={`w-4 h-4 ${normalizing ? 'animate-spin' : ''}`} />
                {normalizing ? 'Normalizando...' : 'Normalizar Todos'}
              </button>
            )}

            {report && (
              <button
                onClick={downloadReport}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Baixar Relatório
              </button>
            )}
          </div>
        </div>
      </div>

      {report && (
        <>
          {/* Métricas Principais */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total de Pacientes</p>
                  <p className="text-3xl font-bold text-gray-900">{report.totalPatients}</p>
                </div>
                <Database className="w-10 h-10 text-gray-400" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Saúde dos Dados</p>
                  <p className="text-3xl font-bold text-green-600">{healthPercentage}%</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-400" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Dados Completos</p>
                  <p className="text-3xl font-bold text-green-600">{report.patientsHealthy}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Com Problemas</p>
                  <p className="text-3xl font-bold text-red-600">{report.patientsWithIssues}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-400" />
              </div>
            </div>
          </div>

          {/* Problemas por Tipo */}
          <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Distribuição de Problemas</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">Campos Faltando</p>
                <p className="text-2xl font-bold text-red-600">
                  {report.issuesByType.missingFields}
                </p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-600">Inconsistências</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {report.issuesByType.inconsistentFields}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Campos Vazios</p>
                <p className="text-2xl font-bold text-blue-600">
                  {report.issuesByType.emptyFields}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Sem Dados Financeiros</p>
                <p className="text-2xl font-bold text-purple-600">
                  {report.issuesByType.noFinancialData}
                </p>
              </div>
            </div>
          </div>

          {/* Auditoria de Agendamentos */}
          {report.appointmentAudit && (
            <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Auditoria de Agendamentos</h3>
                  <p className="text-sm text-gray-500">Tabela `appointments`</p>
                </div>
                <Calendar className="w-6 h-6 text-blue-500" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total de eventos</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {report.appointmentAudit.totalEvents}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Com patientId</p>
                  <p className="text-2xl font-bold text-green-600">
                    {report.appointmentAudit.withPatientId}
                  </p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-gray-600">Sem patientId</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {report.appointmentAudit.withoutPatientId}
                  </p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-gray-600">patientId desconhecido</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {report.appointmentAudit.unknownPatientId}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600">Sem data ou status</p>
                  <p className="text-2xl font-bold text-red-600">
                    {report.appointmentAudit.missingStart + report.appointmentAudit.missingStatus}
                  </p>
                </div>
                <div className="p-4 bg-rose-50 rounded-lg">
                  <p className="text-sm text-gray-600">Sem local</p>
                  <p className="text-2xl font-bold text-rose-600">
                    {report.appointmentAudit.missingLocation}
                  </p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-gray-600">Local inválido</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {report.appointmentAudit.invalidLocation}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Data de criação (início)</label>
                  <input
                    type="date"
                    value={appointmentDateFilter.start}
                    onChange={e => setAppointmentDateFilter(prev => ({ ...prev, start: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Data de criação (fim)</label>
                  <input
                    type="date"
                    value={appointmentDateFilter.end}
                    onChange={e => setAppointmentDateFilter(prev => ({ ...prev, end: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2 flex justify-end items-end">
                  <button
                    onClick={() => setAppointmentDateFilter({ start: '', end: '' })}
                    className="px-3 py-2 text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Limpar filtro de datas
                  </button>
                </div>
              </div>

              {filteredAppointmentIssues.length === 0 ? (
                <div className="bg-green-50 border border-green-100 text-green-700 px-4 py-3 rounded-md flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Nenhum evento com problemas dentro do filtro selecionado.
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-center gap-2 border-b">
                    <ClipboardList className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {filteredAppointmentIssues.length} evento(s) com problemas detectados
                    </span>
                  </div>
                  <div className="max-h-64 overflow-auto divide-y">
                    {filteredAppointmentIssues.map(item => (
                      <div key={item.appointmentId} className="px-4 py-3 text-sm text-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">{item.appointmentId}</div>
                          <DataQualityBadge severity={item.severity} size="sm" />
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {item.patientId ? (
                            <span>ID do paciente: {item.patientId}</span>
                          ) : (
                            <span>Sem vínculo direto com paciente</span>
                          )}
                        </div>
                        {item.patientName && (
                          <div className="text-xs text-gray-500">Nome: {item.patientName}</div>
                        )}
                        {item.startDate && (
                          <div className="text-xs text-gray-500">
                            Início: {new Date(item.startDate).toLocaleString('pt-BR')}
                          </div>
                        )}
                        {item.createdAt && (
                          <div className="text-xs text-gray-500">
                            Criado em: {new Date(item.createdAt).toLocaleString('pt-BR')}
                          </div>
                        )}
                        {item.appointmentLocation != null && (
                          <div className="text-xs text-gray-500">
                            Local atual: {item.appointmentLocation || '(vazio)'}
                          </div>
                        )}
                        <div className="mt-2 text-xs text-red-600">
                          Campos pendentes: {item.missingFields.join(', ')}
                        </div>
                        {item.suggestedPatientId && !item.patientId && item.missingFields.some(field => field.startsWith('patientId')) && (
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => handleFixAppointmentIssue(item)}
                              disabled={fixingAppointment === item.appointmentId}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                              {fixingAppointment === item.appointmentId ? 'Corrigindo...' : 'Corrigir patientId'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filtros e Busca */}
          {report.patientsWithIssues > 0 && (
            <>
              <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Search className="w-4 h-4 inline mr-1" />
                      Buscar Paciente
                    </label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Nome, ID ou campo..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="w-full md:w-64">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Filter className="w-4 h-4 inline mr-1" />
                      Filtrar por Severidade
                    </label>
                    <select
                      value={filter}
                      onChange={e => setFilter(e.target.value as FilterType)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Todos ({report.issues.length})</option>
                      <option value="critical">
                        Críticos ({report.issues.filter(i => i.severity === 'critical').length})
                      </option>
                      <option value="warning">
                        Avisos ({report.issues.filter(i => i.severity === 'warning').length})
                      </option>
                      <option value="info">
                        Info ({report.issues.filter(i => i.severity === 'info').length})
                      </option>
                    </select>
                  </div>
                </div>

                <div className="mt-4 text-sm text-gray-600">
                  Mostrando {filteredIssues.length} de {report.issues.length} problema(s)
                </div>
              </div>

              {/* Lista de Problemas */}
              <div className="bg-white rounded-xl border shadow-sm">
                <div className="p-6 border-b">
                  <h3 className="text-lg font-semibold">Detalhes dos Problemas</h3>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {filteredIssues.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p>Nenhum problema encontrado com os filtros atuais</p>
                    </div>
                  ) : (
                    filteredIssues.map(issue => (
                      <div
                        key={issue.patientId}
                        className="p-4 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedIssue(issue)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-medium text-gray-900">{issue.patientName}</h4>
                              <DataQualityBadge severity={issue.severity} size="sm" />
                            </div>

                            <div className="text-sm text-gray-600 space-y-1">
                              {issue.missingFields.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                                  <span>
                                    <strong>Faltando:</strong> {issue.missingFields.join(', ')}
                                  </span>
                                </div>
                              )}

                              {issue.inconsistentFields.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                                  <span>
                                    <strong>Inconsistências:</strong>{' '}
                                    {issue.inconsistentFields.join(', ')}
                                  </span>
                                </div>
                              )}

                              {issue.emptyFields.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-blue-500 mt-0.5" />
                                  <span>
                                    <strong>Vazios:</strong> {issue.emptyFields.join(', ')}
                                  </span>
                                </div>
                              )}

                              <div className="flex items-center gap-2 mt-2">
                                <Database className="w-4 h-4 text-gray-400" />
                                <span>
                                  Estrutura Financeira:{' '}
                                  <span
                                    className={
                                      issue.financialStructure === 'complete'
                                        ? 'text-green-600 font-medium'
                                        : issue.financialStructure === 'partial'
                                        ? 'text-yellow-600 font-medium'
                                        : 'text-red-600 font-medium'
                                    }
                                  >
                                    {issue.financialStructure === 'complete'
                                      ? 'Completa'
                                      : issue.financialStructure === 'partial'
                                      ? 'Parcial'
                                      : 'Ausente'}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={e => {
                              e.stopPropagation();
                              fixSinglePatient(issue.patientId);
                            }}
                            className="ml-4 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Wrench className="w-3 h-3" />
                            Corrigir
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          {/* Estado Saudável */}
          {report.patientsWithIssues === 0 && !hasAppointmentIssues && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-green-900 mb-2">
                🎉 Todos os Dados Estão Completos!
              </h3>
              <p className="text-green-700">
                Todos os {report.totalPatients} pacientes possuem dados consistentes e completos.
              </p>
            </div>
          )}
        </>
      )}

      {/* Modal de Detalhes */}
      {selectedIssue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold">{selectedIssue.patientName}</h3>
                <p className="text-sm text-gray-600 mt-1">ID: {selectedIssue.patientId}</p>
              </div>
              <button
                onClick={() => setSelectedIssue(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <DataQualityBadge severity={selectedIssue.severity} />
              </div>

              {selectedIssue.missingFields.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
                    <XCircle className="w-5 h-5" />
                    Campos Faltando ({selectedIssue.missingFields.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-red-800 space-y-1">
                    {selectedIssue.missingFields.map(field => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedIssue.inconsistentFields.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Inconsistências ({selectedIssue.inconsistentFields.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                    {selectedIssue.inconsistentFields.map(field => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedIssue.emptyFields.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Campos Vazios ({selectedIssue.emptyFields.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                    {selectedIssue.emptyFields.map(field => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Estrutura Financeira
                </h4>
                <p className="text-sm text-gray-700">
                  Status:{' '}
                  <span
                    className={
                      selectedIssue.financialStructure === 'complete'
                        ? 'text-green-600 font-medium'
                        : selectedIssue.financialStructure === 'partial'
                        ? 'text-yellow-600 font-medium'
                        : 'text-red-600 font-medium'
                    }
                  >
                    {selectedIssue.financialStructure === 'complete'
                      ? 'Completa'
                      : selectedIssue.financialStructure === 'partial'
                      ? 'Parcial'
                      : 'Ausente'}
                  </span>
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    fixSinglePatient(selectedIssue.patientId);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Wrench className="w-4 h-4" />
                  Corrigir Este Paciente
                </button>
                <button
                  onClick={() => setSelectedIssue(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
